import { MessageSquare } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { trackEvent } from '../../../lib/gtag'
import type { ReplyFocus } from '../../../routes/app/startup/$id/outreach.replies'
import { useMarkRepliesSeen } from '../hooks/use-mark-replies-seen'
import {
  useArchiveContactMutation,
  useArchivePortfolioMessageMutation,
} from '../hooks/use-outreach-archive'
import { useOutreachReplies } from '../hooks/use-outreach-replies'
import { EngagedTable, type RosterMatch } from './outreach-active-view'
import { OutreachOutcomesSummary } from './outreach-outcomes-summary'

/**
 * What the outreach produced, on one page: the headline numbers and confirmed
 * calls/intros up top, then every reply from both campaigns.
 *
 * This is the only place replies live — the campaign boards deliberately don't
 * repeat them, so "who has answered me" is a single destination rather than
 * something to hunt for by switching channels.
 */
export function OutreachOutcomesView({
  startupId,
  focus,
}: {
  startupId: string
  /** What the card that sent the founder here promised — see ReplyFocus. */
  focus?: ReplyFocus
}) {
  const replies = useOutreachReplies(startupId)
  const archiveContact = useArchiveContactMutation(startupId)
  const archivePortfolioMessage = useArchivePortfolioMessageMutation(startupId)
  // Optimistic hide while the mutation runs, so a row doesn't linger after the
  // founder archives it. Both campaigns share the map — ids don't collide.
  const [archiving, setArchiving] = useState<Record<string, boolean>>({})

  // Replies are marked read one at a time, as each row is actually looked at —
  // see useMarkRepliesSeen. Opening the page used to stamp a watermark that
  // cleared every reply at once, including the ones below the fold.
  const { observeRow } = useMarkRepliesSeen(startupId)

  const markArchiving = (id: string, value: boolean) =>
    setArchiving((prev) => {
      if (value) return { ...prev, [id]: true }
      const next = { ...prev }
      delete next[id]
      return next
    })

  const archiveInvestor = (entry: RosterMatch) => {
    const leadId = entry.leadId
    if (!leadId) return
    markArchiving(leadId, true)
    archiveContact.mutate(leadId, {
      onSuccess: () =>
        trackEvent('outreach_lead_archived', {
          startup_id: startupId,
          investor_id: entry.investorId,
        }),
      onSettled: () => markArchiving(leadId, false),
    })
  }

  const archiveWarmIntro = (entry: RosterMatch) => {
    const messageId = entry.leadId
    if (!messageId) return
    markArchiving(messageId, true)
    archivePortfolioMessage.mutate(messageId, {
      onSettled: () => markArchiving(messageId, false),
    })
  }

  const notArchiving = (entry: RosterMatch) => !archiving[entry.leadId ?? '']
  const investorReplies = replies.investors.filter(notArchiving)
  const warmIntroReplies = replies.portfolio.filter(notArchiving)

  // The first unread reply across both campaigns, in the order they are drawn.
  // Only one row is scrolled to — landing between two sections would show
  // neither properly.
  const firstUnreadId = [...investorReplies, ...warmIntroReplies].find(
    (entry) => entry.unread
  )?.messageId

  // Scroll once, on the row itself rather than the section, so the founder sees
  // the reply and not the heading above it. `focusUnread` is what makes this a
  // response to "reply now" instead of a page that yanks itself around on every
  // visit.
  const scrolled = useRef(false)
  const focusRow = useCallback((node: HTMLElement | null) => {
    if (!node || scrolled.current) return
    scrolled.current = true
    node.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [])
  // An outcome is above the replies, so it takes the scroll when there is one.
  // While a call is still being verified there is no confirmed card yet, and
  // the reply behind it is the closest thing to what the card promised.
  const focusTarget = focus ? firstUnreadId : undefined

  return (
    <div className="mx-auto max-w-6xl py-2">
      <div className="mb-6">
        <h1 className="font-bold text-[22px] text-foreground tracking-[-0.01em]">Outcomes</h1>
        <p className="mt-1 text-[13px] text-muted-foreground leading-relaxed">
          What your campaigns have produced, across both channels. Take a conversation over in
          LinkedIn, then archive the contact once you've handled it.
        </p>
      </div>

      <OutreachOutcomesSummary
        startupId={startupId}
        onFocusOutcome={focus === 'outcome' ? focusRow : undefined}
      />

      {replies.total === 0 ? (
        <EmptyReplies />
      ) : (
        <div className="space-y-8">
          {investorReplies.length > 0 && (
            <section>
              <h2 className="mb-3 font-semibold text-[15px] text-foreground">
                Investor campaign
                <span className="ml-2 font-normal text-[13px] text-muted-foreground">
                  {investorReplies.length}
                </span>
              </h2>
              <EngagedTable
                entries={investorReplies}
                isPaused={false}
                onArchive={archiveInvestor}
                archiving={archiving}
                observe={(entry) => observeRow(entry.messageId, 'investor')}
                focusId={focusTarget}
                onFocusRow={focusRow}
              />
            </section>
          )}

          {warmIntroReplies.length > 0 && (
            <section>
              <h2 className="mb-3 font-semibold text-[15px] text-foreground">
                Warm intro campaign
                <span className="ml-2 font-normal text-[13px] text-muted-foreground">
                  {warmIntroReplies.length}
                </span>
              </h2>
              <EngagedTable
                entries={warmIntroReplies}
                isPaused={false}
                subject="Founders who replied or agreed to intro you to their fund"
                onArchive={archiveWarmIntro}
                archiving={archiving}
                observe={(entry) => observeRow(entry.messageId, 'portfolio')}
                focusId={focusTarget}
                onFocusRow={focusRow}
              />
            </section>
          )}
        </div>
      )}
    </div>
  )
}

function EmptyReplies() {
  return (
    <div className="rounded-2xl border border-border/60 border-dashed bg-card/30 px-6 py-14 text-center">
      <div className="mx-auto flex size-11 items-center justify-center rounded-xl bg-muted/40">
        <MessageSquare className="size-5 text-muted-foreground" />
      </div>
      <p className="mt-4 font-semibold text-[15px] text-foreground">No replies yet</p>
      <p className="mx-auto mt-1 max-w-sm text-[13px] text-muted-foreground leading-relaxed">
        Replies from your investor and warm intro campaigns land here as soon as they arrive.
      </p>
    </div>
  )
}
